import type { LessonRef } from "../../../lib/api/fieldApi";
import { hasMessage } from "../../../i18n";
import type { Locale, Translate } from "../../../i18n";

/**
 * 표시 이름 — 지금 계약의 title 은 모형 slug 다(사람이 읽는 제목이 아직 없음). slug 처럼 생긴 제목은
 * 모형 이름 카탈로그(student.models.*)로 바꾸고, 카탈로그에 없으면 slug 를 사람이 읽기 좋게 편다.
 */
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const humanize = (slug: string) => slug.split("-").filter(Boolean).join(" ");

export function modelName(
  t: Translate,
  locale: Locale,
  slug: string,
  title?: string | null,
): string {
  if (title && title !== slug && !SLUG.test(title)) return title;
  const key = `student.models.${slug}`;
  return hasMessage(key, locale) ? t(key) : humanize(title || slug);
}

/** 차시 제목 — Campus 표시 문구(로캘 → ko 순). 없으면 null */
export function lessonTitle(
  _t: Translate,
  locale: Locale,
  lesson: LessonRef | null | undefined,
): string | null {
  const title = lesson?.title;
  if (!title || typeof title !== "object") return null;
  return (title[locale] ?? title.ko ?? "").trim() || null;
}

/** 상단 바 차시 칩 '5차시 · 오르막 자동차'(SCR v0.6 FS-01 와이어). 차시 번호가 없으면 null */
export function lessonChip(
  t: Translate,
  locale: Locale,
  lesson: LessonRef | null | undefined,
): string | null {
  if (lesson?.no == null) return null;
  const title = lessonTitle(t, locale, lesson);
  return title
    ? t("student.lesson.chip", { no: lesson.no, title })
    : t("student.lesson.no", { no: lesson.no });
}

/**
 * 작품 이름 — 서버 자동 이름이 있으면 그대로. 없으면 SCR v0.6 규칙 '차시 + 별명'(예: 5차시 오르막 자동차 · 별똥이),
 * 차시를 모르면 '모형 이름 · 별명'.
 */
export function workTitle(
  t: Translate,
  locale: Locale,
  w: { title: string; modelSlug: string; lesson?: LessonRef | null },
  displayName: string,
): string {
  if (w.title && w.title !== w.modelSlug && !SLUG.test(w.title)) return w.title;
  const lt = lessonTitle(t, locale, w.lesson);
  const name =
    w.lesson?.no != null && lt
      ? t("student.lesson.work", { no: w.lesson.no, title: lt })
      : modelName(t, locale, w.modelSlug, w.title);
  return displayName ? t("student.upload.workName", { title: name, name: displayName }) : name;
}
